import { useMemo } from 'react'
import { isEmpty } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Layout } from '@/shared/components/ui/layout'
import { Loader } from '@/shared/components/ui/loader'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { DatasheetProvider } from '@/shared/lib/datasheet/context'
import { useAnchorsTableConfigStore } from '@/shared/lib/table-config'
import { getInitialTableState } from '@/shared/tools/columns'

import { geColumnsSchema } from '../lib/columns'
import AnchorsAssortmentTable from './AnchorAssortmentTable/AnchorAssortmentTable'
import AnchorManagementPageFooter from './AnchorManagementPageFooter'
import AnchorManagementPageToolbar from './AnchorManagementPageToolbar'
import {
	AnchorsManagementProvider,
	useAnchorsManagementContext,
} from './AnchorManagementProvider'
import AnchorProductDetailDrawer from './AnchorProductDetailDrawer/AnchorProductDetail'

export function AnchorManagementPage() {
	const storedUserConfigs = useAnchorsTableConfigStore((state) => ({
		columnVisibility: state.columnVisibility,
		columnPinning: state.columnPinning,
		columnOrder: state.columnOrder,
		columnSizing: state.columnSizing,
	}))
	const initialState = useMemo(() => {
		const schema = geColumnsSchema()
		const defaultPreset = getInitialTableState(schema)

		if (!isEmpty(storedUserConfigs)) {
			return {
				...storedUserConfigs,
				defaultPreset,
				schema,
				rawSchema: schema,
			}
		} else {
			return {
				...defaultPreset,
				defaultPreset,
				schema,
				rawSchema: schema,
			}
		}
	}, [storedUserConfigs])

	return (
		<DatasheetProvider
			{...initialState}
			datasheetId='anchor-management-products'
		>
			<AnchorsManagementProvider>
				<AnchorManagementLayout />
			</AnchorsManagementProvider>
		</DatasheetProvider>
	)
}

function AnchorManagementLayout() {
	const { t } = useTranslation()

	const [{ selected: selectedSku }, setQuery] = useLocationQuery()
	const { isError, isFetching } = useAnchorsManagementContext()

	return (
		<Layout>
			<AnchorManagementPageToolbar />
			<Layout className='relative flex size-full h-full flex-col overflow-hidden px-4'>
				<AnchorProductDetailDrawer
					onClose={() =>
						setQuery({
							selected: null,
						})
					}
					selectedSku={selectedSku}
				/>
				{isError && (
					<Alert variant='destructive' className='mt-2'>
						<AlertDescription>{t('assortment.table.error')}</AlertDescription>
					</Alert>
				)}
				<Blanket
					className='absolute flex flex-col items-center justify-center space-y-5 rounded-lg bg-transparent'
					isTinted={isFetching}
				>
					<div className='z-50 rounded-lg bg-white/50 p-5 shadow backdrop-blur-md fade-in dark:bg-black/50'>
						<Loader />
					</div>
				</Blanket>
				<AnchorsAssortmentTable />
				<AnchorManagementPageFooter />
			</Layout>
		</Layout>
	)
}
